import { Navigate, Route, Router, useParams } from "@solidjs/router";
import type { JSX } from "solid-js";
import { AppRoot } from "./AppRoot";
import { BriefWorkspace } from "./features/brief/BriefWorkspace";
import {
  EvalBatchRoute,
  EvalComparisonRoute,
  EvalSuiteRoute,
} from "./features/evals/EvalRoutes";
import { EvalSuiteList } from "./features/evals/EvalSuiteList";
import { GovernRoute } from "./features/govern/GovernRoute";
import { Landing } from "./features/landing/Landing";
import { ScanMap } from "./features/map/ScanMap";
import { DraftEditor } from "./features/playground/DraftEditor";
import { PlaygroundList } from "./features/playground/PlaygroundList";
import { PlaygroundRunDetail } from "./features/playground/PlaygroundRunDetail";
import { PlaygroundWorkspace } from "./features/playground/PlaygroundWorkspace";
import { PromptBundleEditor } from "./features/playground/PromptBundleEditor";
import { PromptBundleList } from "./features/playground/PromptBundleList";
import { RunWorkbench } from "./features/playground/RunWorkbench";
import { RunDetail } from "./features/runs/RunDetail";
import { RunsList } from "./features/runs/RunsList";
import { ComingSoon } from "./features/scan/ComingSoon";
import { ScanIssueDetail } from "./features/scan/IssueDetail";
import { ScanAiReference } from "./features/scan/ScanAiReference";
import { ScanBrandAuthoring } from "./features/scan/ScanBrandAuthoring";
import { ScanBrandGuidelines } from "./features/scan/ScanBrandGuidelines";
import { ScanContentFitnessDimension } from "./features/scan/ScanContentFitnessDimension";
import { ScanHelp } from "./features/scan/ScanHelp";
import { ScanIssues } from "./features/scan/ScanIssues";
import { ScanJourneys } from "./features/scan/ScanJourneys";
import { ScanMetricRoute } from "./features/scan/ScanMetricRoute";
import { ScanPageDetail } from "./features/scan/ScanPageDetail";
import { ScanPages } from "./features/scan/ScanPages";
import { ScanSignals } from "./features/scan/ScanSignals";
import { ScanSummary } from "./features/scan/ScanSummary";
import { SignalOverview } from "./features/scan/SignalOverview";
import { WebsiteSettings } from "./features/settings/WebsiteSettings";
import { SocialReport } from "./features/social/SocialReport";

// JSX props compile to lazy getters, so `useParams()` inlined in a prop runs
// on every prop READ — including reads inside event handlers, where there is
// no router context and the primitive throws ("<A> and 'use' router
// primitives can be only used inside a Route", 2026-07-11). `page` calls
// useParams once at component setup and hands the render function the
// reactive params proxy, which is safe to read anywhere.
function page(
  render: (params: Partial<Record<string, string>>) => JSX.Element,
): () => JSX.Element {
  return () => render(useParams());
}

export function App() {
  return (
    <Router root={AppRoot}>
      <Route path="/" component={Landing} />
      <Route path="/playground" component={PlaygroundList} />
      <Route path="/evals" component={EvalSuiteList} />
      <Route path="/evals/suites/:id" component={EvalSuiteRoute} />
      <Route path="/evals/batches/:id" component={EvalBatchRoute} />
      <Route path="/evals/comparisons/:id" component={EvalComparisonRoute} />
      <Route path="/playground/prompts" component={PromptBundleList} />
      <Route
        path="/playground/prompts/new"
        component={() => <PromptBundleEditor />}
      />
      <Route
        path="/playground/prompts/:bundleId"
        component={page((p) => (
          <PromptBundleEditor bundleId={p.bundleId ?? ""} />
        ))}
      />
      <Route path="/playground/drafts/new" component={() => <DraftEditor />} />
      <Route
        path="/playground/drafts/:draftId"
        component={page((p) => <DraftEditor draftId={p.draftId ?? ""} />)}
      />
      <Route
        path="/playground/w/:websiteId/runs/:runId"
        component={page((p) => (
          <PlaygroundRunDetail
            websiteId={p.websiteId ?? ""}
            runId={p.runId ?? ""}
          />
        ))}
      />
      <Route
        path="/playground/w/:websiteId"
        component={page((p) => <RunWorkbench websiteId={p.websiteId ?? ""} />)}
      />
      <Route
        path="/playground/:clientId"
        component={page((p) => (
          <PlaygroundWorkspace clientId={p.clientId ?? ""} />
        ))}
      />
      <Route
        path="/w/:id"
        component={page((p) => <Navigate href={`/w/${p.id}/overview`} />)}
      />
      <Route
        path="/w/:id/overview"
        component={page((p) => <SignalOverview websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan"
        component={page((p) => <ScanSummary websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/map"
        component={page((p) => <ScanMap websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/brief"
        component={page((p) => <BriefWorkspace websiteId={p.id ?? ""} />)}
      />
      <Route path="/w/:id/govern" component={GovernRoute} />
      <Route
        path="/w/:id/social"
        component={page((p) => <SocialReport websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/settings"
        component={page((p) => <WebsiteSettings websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/help"
        component={page((p) => <ScanHelp websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/runs"
        component={page((p) => <RunsList websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/runs/:runId"
        component={page((p) => (
          <RunDetail websiteId={p.id ?? ""} runId={p.runId ?? ""} />
        ))}
      />
      <Route
        path="/w/:id/scan/pages"
        component={page((p) => <ScanPages websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/pages/:pageId"
        component={page((p) => (
          <ScanPageDetail websiteId={p.id ?? ""} pageId={p.pageId ?? ""} />
        ))}
      />
      <Route
        path="/w/:id/scan/content-fitness/:dimensionKey"
        component={page((p) => (
          <ScanContentFitnessDimension
            websiteId={p.id ?? ""}
            dimensionKey={p.dimensionKey ?? ""}
          />
        ))}
      />
      <Route
        path="/w/:id/scan/issues"
        component={page((p) => <ScanIssues websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/issues/:issueId"
        component={page((p) => (
          <ScanIssueDetail websiteId={p.id ?? ""} issueId={p.issueId ?? ""} />
        ))}
      />
      <Route
        path="/w/:id/scan/journeys"
        component={page((p) => <ScanJourneys websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/ai-reference"
        component={page((p) => <ScanAiReference websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/brand-guidelines"
        component={page((p) => <ScanBrandGuidelines websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/brand-guidelines/manage"
        component={page((p) => <ScanBrandAuthoring websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/estate"
        component={page((p) => (
          <Navigate href={`/w/${p.id}/scan/pages?focus=estate`} />
        ))}
      />
      <Route
        path="/w/:id/coming-soon/help"
        component={page((p) => <Navigate href={`/w/${p.id}/help`} />)}
      />
      <Route
        path="/w/:id/coming-soon/:module"
        component={page((p) => (
          <ComingSoon websiteId={p.id ?? ""} module={p.module ?? ""} />
        ))}
      />
      <Route
        path="/w/:id/issues"
        component={page((p) => <Navigate href={`/w/${p.id}/scan/issues`} />)}
      />
      <Route
        path="/w/:id/scan/signals"
        component={page((p) => <ScanSignals websiteId={p.id ?? ""} />)}
      />
      <Route
        path="/w/:id/scan/metrics/:key"
        component={page((p) => (
          <ScanMetricRoute websiteId={p.id ?? ""} metricKey={p.key ?? ""} />
        ))}
      />
      <Route
        path="/w/:id/signals"
        component={page((p) => <Navigate href={`/w/${p.id}/scan/signals`} />)}
      />
      <Route
        path="/w/:id/pages"
        component={page((p) => <Navigate href={`/w/${p.id}/scan/pages`} />)}
      />
      <Route
        path="/w/:id/pages/:pageId"
        component={page((p) => (
          <Navigate href={`/w/${p.id}/scan/pages/${p.pageId ?? ""}`} />
        ))}
      />
      <Route
        path="/w/:id/m/:key"
        component={page((p) => (
          <Navigate href={`/w/${p.id}/scan/metrics/${p.key ?? ""}`} />
        ))}
      />
      <Route
        path="/w/:id/tov"
        component={page((p) => (
          <Navigate href={`/w/${p.id}/scan/brand-guidelines/manage`} />
        ))}
      />
      <Route
        path="*path"
        component={() => (
          <div class="route-missing">No route for {location.pathname}</div>
        )}
      />
    </Router>
  );
}
